import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Search, X, Copy, Check } from 'lucide-react';
import { listPrompts, deletePrompt } from 'features/prompts/api/promptsApi';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import BulkBar from 'shared/components/tables/BulkBar';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import TableSkeleton from 'shared/components/ui/TableSkeleton';
import Avatar from 'shared/components/ui/Avatar';
import { shortHash } from 'features/prompts/lib/promptMembers';
import {
  activitySearchWrapperClass,
  activitySearchInputClass,
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

function timeAgo(dateStr) {
  if (!dateStr) return '-';
  const secs = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

const DESCRIPTION_MAX = 64;

/** Fixed-length, single-line truncation with an ellipsis — not CSS clipping,
 *  so the cut point is the same regardless of column width. */
function truncate(str, max = DESCRIPTION_MAX) {
  const s = String(str || '');
  return s.length > max ? `${s.slice(0, max).trimEnd()}…` : s;
}

/** A small "copy to clipboard" icon button that flips to a check mark briefly. */
function CopyButton({ value, label, className = '' }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) { /* clipboard blocked */ }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={label ? `Copy ${label}` : 'Copy'}
      className={`inline-flex shrink-0 items-center rounded p-0.5 text-[color:var(--text-disabled)] hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] ${className}`}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

const COLUMNS = ['Name', 'Description', 'Owner', 'Labels', 'Latest hash', 'Updated'];

function LabelChip({ label }) {
  return (
    <span className="rounded border border-[color:var(--border-default)] px-1.5 py-0.5 text-[10.5px] font-medium text-[color:var(--text-secondary)]">
      {label}
    </span>
  );
}

export default function PromptsListPage() {
  useBreadcrumb([{ label: 'Prompts' }]);

  const { confirm: appConfirm } = useAppDialog();
  const { team } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listPrompts());
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [items, query]);

  // "New prompt" opens the editor on an unsaved draft — the name is typed in
  // the editor itself and the record isn't created until the first save. No
  // second dialog, and no half-made prompt left behind if you walk away.
  const handleCreate = () => navigate(`/${encodeURIComponent(team)}/prompts/new`);

  // Only a prompt's owner may delete it, so only owned rows can be ticked.
  const deletable = useMemo(() => filtered.filter((p) => p.is_owner), [filtered]);
  // A reload (or a search) can drop rows the selection still holds.
  useEffect(() => { setPicked((cur) => new Set([...cur].filter((id) => items.some((r) => r.id === id && r.is_owner)))); }, [items]);
  const chosen = items.filter((r) => picked.has(r.id) && r.is_owner);
  const allPicked = deletable.length > 0 && deletable.every((r) => picked.has(r.id));
  const toggleOne = (id) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const removeChosen = async () => {
    if (!chosen.length) return;
    const many = chosen.length > 1;
    const ok = await appConfirm(
      many ? `${chosen.length} prompts will be removed.` : `"${chosen[0].name}" will be removed.`,
      { title: many ? 'Delete prompts' : 'Delete prompt', confirmLabel: 'Delete', danger: true },
    );
    if (!ok) return;
    setBusy(true);
    const res = await Promise.allSettled(chosen.map((r) => deletePrompt(r.id)));
    const failed = res.filter((x) => x.status === 'rejected');
    setPicked(new Set());
    await refresh();
    if (failed.length) setError(failed[0].reason?.response?.data?.error || failed[0].reason?.message || 'Something failed');
    setBusy(false);
  };

  return (
    <main
      className="relative flex flex-1 min-h-0 flex-col overflow-hidden bg-surface"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <div className="flex shrink-0 flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
          <div className="flex items-center gap-1.5 px-3 text-[12px]">
            <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
              {loading ? '…' : filtered.length.toLocaleString()}
            </span>
            <span className="text-[color:var(--text-muted)]">
              {filtered.length === 1 ? 'prompt' : 'prompts'}
            </span>
          </div>
        </div>

        <div className={`${activitySearchWrapperClass} ml-auto w-[220px]`}>
          <Search size={13} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search prompts…"
            className={activitySearchInputClass}
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="flex-none text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
              aria-label="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>

        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[color:var(--trodo-primary)] px-3 text-[12px] font-medium text-[color:var(--text-on-brand)] hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
        >
          <Plus className="w-3.5 h-3.5" />
          New prompt
        </button>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-[color:var(--feedback-error-bg)] border border-[color:var(--feedback-error)] text-[12.5px] text-[color:var(--feedback-error)]">
          {error}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-8 pt-3">
        {loading ? (
          <TableSkeleton columns={COLUMNS} rows={6} />
        ) : (
          <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
            <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className={tableHeaderRowClass}>
                  <th scope="col" className={`${tableHeaderCellClass} w-8`}>
                    {canWrite && deletable.length > 0 && (
                      <input type="checkbox" checked={allPicked} aria-label={allPicked ? 'Clear the selection' : 'Select all'}
                        onChange={() => setPicked(allPicked ? new Set() : new Set(deletable.map((r) => r.id)))} />
                    )}
                  </th>
                  {COLUMNS.map((c) => <th key={c} className={tableHeaderCellClass}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="px-3 py-10 text-center text-[12px] text-[color:var(--text-muted)]">
                      {query.trim() ? `No prompts match "${query.trim()}"` : 'No prompts yet'}
                    </td>
                  </tr>
                )}
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    className={tableRowClass}
                    onClick={() => navigate(`/${encodeURIComponent(team)}/prompts/${p.id}`)}
                  >
                    <td className={`${tableDataCellClass} w-8`} onClick={(e) => e.stopPropagation()}>
                      {canWrite && (
                        <input type="checkbox" checked={picked.has(p.id)} disabled={!p.is_owner}
                          title={p.is_owner ? undefined : 'Only the owner can delete'}
                          aria-label={`Select ${p.name}`} onChange={() => toggleOne(p.id)} />
                      )}
                    </td>
                    <td className={tableDataCellClass}>
                      <span className="group/name inline-flex min-w-0 max-w-full items-center gap-1">
                        <span className="truncate font-medium">{p.name}</span>
                        <span className="opacity-0 transition-opacity group-hover/name:opacity-100">
                          <CopyButton value={p.name} label="name" />
                        </span>
                      </span>
                    </td>
                    <td className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>
                      {p.description
                        ? <span title={p.description}>{truncate(p.description)}</span>
                        : <span className="text-[color:var(--text-disabled)]">-</span>}
                    </td>
                    <td className={tableDataCellClass}>
                      {p.creator_name ? (
                        <span className="inline-flex items-center gap-2">
                          <Avatar name={p.creator_name} src={p.creator_avatar_url} size={20} />
                          <span className="truncate text-[color:var(--text-primary)]">{p.creator_name}</span>
                        </span>
                      ) : <span className="text-[color:var(--text-disabled)]">-</span>}
                    </td>
                    <td className={tableDataCellClass}>
                      <span className="flex flex-nowrap gap-1 whitespace-nowrap">
                        {(p.labels || []).length === 0
                          ? <span className="text-[color:var(--text-disabled)]">-</span>
                          : (p.labels || []).map((l) => <LabelChip key={l} label={l} />)}
                      </span>
                    </td>
                    <td className={tableDataCellClass}>
                      {p.latest_version_hash ? (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap font-mono text-[11px] text-[color:var(--text-muted)]">
                          {shortHash(p.latest_version_hash)}
                          <CopyButton value={p.latest_version_hash} label="hash" />
                        </span>
                      ) : (
                        <span className="text-[color:var(--text-disabled)]">-</span>
                      )}
                    </td>
                    <td className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>
                      {timeAgo(p.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
      <BulkBar count={canWrite ? picked.size : 0} label="Selected prompts" onClear={() => setPicked(new Set())} actions={[
        { label: 'Delete', disabled: busy, onClick: removeChosen, danger: true },
      ]} />
    </main>
  );
}
